// =============================================
// COMPONENTE: NOTIFICATION CENTER
// =============================================
// Arquivo: NotificationCenter.tsx
// Descrição: Centro de notificações em tempo real com toast e histórico
// =============================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Check, AlertCircle, Info, CheckCircle, XCircle,
  Trash2
} from 'lucide-react';
import './NotificationCenter.css';
import { apiService } from '../../services/api';
import { getToken } from '../../services/config';

// =============================================
// INTERFACES
// =============================================

interface NotificationCenterProps {
  maxToasts?: number;
  toastDuration?: number;
  enableSound?: boolean;
  enableDesktop?: boolean;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  category: 'execution' | 'system' | 'queue' | 'user' | 'performance';
  actionable?: boolean;
  actionLabel?: string;
  actionCallback?: () => void;
  metadata?: any;
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  maxToasts = 3,
  toastDuration = 5000,
  enableSound = true,
  enableDesktop = false,
  position = 'top-right'
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [toasts, setToasts] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | Notification['category']>('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptRef = useRef<number>(0);
  const isConnectingRef = useRef<boolean>(false);

  // =============================================
  // NOTIFICATION SOUND
  // =============================================

  useEffect(() => {
    if (enableSound) {
      // Create notification sound (simple beep)
      const audioContext = new AudioContext();
      audioContext.createOscillator();
      audioContext.createGain();

      audioRef.current = new Audio();
    }
  }, [enableSound]);

  // =============================================
  // REQUEST DESKTOP PERMISSION
  // =============================================

  useEffect(() => {
    if (enableDesktop && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, [enableDesktop]);

  // =============================================
  // FETCH NOTIFICATIONS FROM API
  // =============================================

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await apiService.getNotifications({ limit: 50 });
      if (response.success && response.data) {
        // Mapear notificações do backend para o formato do frontend
        const mappedNotifications: Notification[] = response.data.map((notif: any) => ({
          id: notif.id.toString(),
          type: mapTypeToFrontend(notif.tipo),
          title: notif.titulo,
          message: notif.mensagem,
          timestamp: new Date(notif.data_envio),
          read: notif.lida,
          category: mapTipoToCategory(notif.tipo),
          metadata: notif.link ? { link: notif.link } : undefined
        }));

        setNotifications(mappedNotifications);
      }
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    }
  }, []);

  // =============================================
  // WEBSOCKET CONNECTION
  // =============================================

  useEffect(() => {
    const MAX_RECONNECT_ATTEMPTS = 10;
    const BASE_RECONNECT_DELAY = 5000; // 5 segundos
    const MAX_RECONNECT_DELAY = 60000; // 60 segundos máximo

    const setupWebSocket = () => {
      // Evitar conexões simultâneas
      if (isConnectingRef.current) {
        return;
      }

      const token = getToken();
      if (!token) {
        return;
      }

      // Verificar se já está conectado
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        return;
      }

      isConnectingRef.current = true;

      // Conectar ao WebSocket - Derivar host da URL da API
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      let wsHost: string;
      let wsProtocol: string;

      try {
        const apiUrl = new URL(apiBaseUrl);
        wsHost = apiUrl.host;
        wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      } catch {
        // Fallback para desenvolvimento
        wsHost = 'localhost:3001';
        wsProtocol = 'ws:';
      }

      const wsUrl = `${wsProtocol}//${wsHost}/ws?token=${token}`;

      try {
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          isConnectingRef.current = false;
          reconnectAttemptRef.current = 0; // Reset contador de reconexões
          // Subscrever aos canais de notificações
          ws.send(JSON.stringify({
            type: 'subscribe',
            channels: ['queue', 'system', 'execution']
          }));
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);

            // Processar notificações em tempo real
            if (message.type === 'message' && message.data.type === 'notification') {
              const notification = message.data.notification;
              addNotification({
                type: notification.type,
                title: notification.title,
                message: notification.message,
                category: notification.category,
                metadata: notification.metadata
              });
            }
          } catch (error) {
            // Ignorar erros de parsing silenciosamente
          }
        };

        ws.onerror = () => {
          isConnectingRef.current = false;
        };

        ws.onclose = () => {
          isConnectingRef.current = false;
          wsRef.current = null;

          // Reconectar com backoff exponencial, até o limite máximo de tentativas
          if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
            reconnectAttemptRef.current += 1;
            const delay = Math.min(
              BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptRef.current - 1),
              MAX_RECONNECT_DELAY
            );

            // Cancelar timeout anterior se existir
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
            }

            reconnectTimeoutRef.current = setTimeout(setupWebSocket, delay);
          }
        };

        wsRef.current = ws;
      } catch (error) {
        isConnectingRef.current = false;
      }
    };

    setupWebSocket();

    // Cleanup ao desmontar
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =============================================
  // LOAD INITIAL NOTIFICATIONS
  // =============================================

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // =============================================
  // TYPE MAPPING FUNCTIONS
  // =============================================

  const mapTypeToFrontend = (tipo: string): Notification['type'] => {
    const typeMap: Record<string, Notification['type']> = {
      'success': 'success',
      'error': 'error',
      'warning': 'warning',
      'info': 'info',
      'execution': 'info',
      'system': 'info',
      'queue': 'warning',
      'user': 'info',
      'performance': 'warning'
    };

    return typeMap[tipo] || 'info';
  };

  const mapTipoToCategory = (tipo: string): Notification['category'] => {
    const categoryMap: Record<string, Notification['category']> = {
      'execution': 'execution',
      'system': 'system',
      'queue': 'queue',
      'user': 'user',
      'performance': 'performance'
    };

    return categoryMap[tipo] || 'system';
  };

  // =============================================
  // ADD NOTIFICATION
  // =============================================

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false
    };

    setNotifications(prev => [newNotification, ...prev]);

    // Add to toast queue
    setToasts(prev => {
      const newToasts = [newNotification, ...prev];
      return newToasts.slice(0, maxToasts);
    });

    // Play sound
    if (enableSound) {
      playNotificationSound();
    }

    // Show desktop notification
    if (enableDesktop && Notification.permission === 'granted') {
      new Notification(newNotification.title, {
        body: newNotification.message,
        icon: getNotificationIcon(newNotification.type),
        tag: newNotification.id
      });
    }

    // Auto-remove toast
    setTimeout(() => {
      removeToast(newNotification.id);
    }, toastDuration);

    return newNotification;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxToasts, toastDuration, enableSound, enableDesktop]);

  // =============================================
  // REMOVE TOAST
  // =============================================

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // =============================================
  // MARK AS READ
  // =============================================

  const markAsRead = useCallback(async (id: string) => {
    try {
      await apiService.markNotificationAsRead(parseInt(id));
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  }, []);

  // =============================================
  // MARK ALL AS READ
  // =============================================

  const markAllAsRead = useCallback(async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
    }
  }, []);

  // =============================================
  // DELETE NOTIFICATION
  // =============================================

  const deleteNotification = useCallback(async (id: string) => {
    try {
      await apiService.deleteNotification(parseInt(id));
      setNotifications(prev => prev.filter(n => n.id !== id));
      removeToast(id);
    } catch (error) {
      console.error('Erro ao deletar notificação:', error);
    }
  }, [removeToast]);

  // =============================================
  // CLEAR ALL
  // =============================================

  const clearAll = useCallback(async () => {
    try {
      await apiService.deleteAllNotifications();
      setNotifications([]);
      setToasts([]);
    } catch (error) {
      console.error('Erro ao limpar todas notificações:', error);
    }
  }, []);

  // =============================================
  // PLAY SOUND
  // =============================================

  const playNotificationSound = () => {
    try {
      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.error('Erro ao reproduzir som:', error);
    }
  };

  // =============================================
  // GET ICON
  // =============================================

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return '/icons/success.png';
      case 'error':
        return '/icons/error.png';
      case 'warning':
        return '/icons/warning.png';
      case 'info':
        return '/icons/info.png';
    }
  };

  // =============================================
  // FILTER NOTIFICATIONS
  // =============================================

  const filteredNotifications = notifications.filter(n => {
    if (filter !== 'all' && n.category !== filter) return false;
    if (showUnreadOnly && n.read) return false;
    return true;
  });

  // =============================================
  // RENDER NOTIFICATION ICON
  // =============================================

  const renderNotificationIcon = (type: Notification['type']) => {
    const iconProps = { size: 20, className: 'notification-type-icon' };

    switch (type) {
      case 'success':
        return <CheckCircle {...iconProps} className="notification-type-icon success" />;
      case 'error':
        return <XCircle {...iconProps} className="notification-type-icon error" />;
      case 'warning':
        return <AlertCircle {...iconProps} className="notification-type-icon warning" />;
      case 'info':
        return <Info {...iconProps} className="notification-type-icon info" />;
    }
  };

  // =============================================
  // RENDER TOAST
  // =============================================

  const renderToast = (notification: Notification) => (
    <div
      key={notification.id}
      className={`notification-toast ${notification.type}`}
    >
      <div className="toast-icon">
        {renderNotificationIcon(notification.type)}
      </div>
      <div className="toast-content">
        <div className="toast-title">{notification.title}</div>
        <div className="toast-message">{notification.message}</div>
      </div>
      <button
        className="toast-close"
        onClick={() => removeToast(notification.id)}
      >
        <X size={16} />
      </button>
    </div>
  );

  // =============================================
  // RENDER NOTIFICATION ITEM
  // =============================================

  const renderNotificationItem = (notification: Notification) => (
    <div
      key={notification.id}
      className={`notification-item ${notification.read ? 'read' : 'unread'}`}
      onClick={() => !notification.read && markAsRead(notification.id)}
    >
      <div className="notification-item-icon">
        {renderNotificationIcon(notification.type)}
      </div>
      <div className="notification-item-content">
        <div className="notification-item-header">
          <span className="notification-item-title">{notification.title}</span>
          <span className="notification-item-time">
            {formatTimeAgo(notification.timestamp)}
          </span>
        </div>
        <div className="notification-item-message">{notification.message}</div>
        {notification.actionable && (
          <button
            className="notification-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              notification.actionCallback?.();
            }}
          >
            {notification.actionLabel || 'Ver detalhes'}
          </button>
        )}
      </div>
      <button
        className="notification-delete-btn"
        onClick={(e) => {
          e.stopPropagation();
          deleteNotification(notification.id);
        }}
      >
        <X size={14} />
      </button>
    </div>
  );

  // =============================================
  // FORMAT TIME AGO
  // =============================================

  const formatTimeAgo = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'agora';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  // =============================================
  // UNREAD COUNT
  // =============================================

  const unreadCount = notifications.filter(n => !n.read).length;

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <>
      {/* Toast Container */}
      <div className={`notification-toast-container ${position}`}>
        {toasts.map(renderToast)}
      </div>

      {/* Notification Bell Button */}
      <div className="notification-bell-container">
        <button
          className="notification-bell-btn"
          onClick={() => setIsOpen(!isOpen)}
        >
          <AlertCircle size={20} />
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </button>

        {/* Notification Panel */}
        {isOpen && (
          <div className="notification-panel">
            <div className="notification-panel-header">
              <h3>Notificações</h3>
              <div className="notification-panel-actions">
                <button
                  className="panel-action-btn"
                  onClick={markAllAsRead}
                  title="Marcar todas como lidas"
                >
                  <Check size={16} />
                </button>
                <button
                  className="panel-action-btn"
                  onClick={clearAll}
                  title="Limpar todas"
                >
                  <Trash2 size={16} />
                </button>
                <button
                  className="panel-action-btn"
                  onClick={() => setIsOpen(false)}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="notification-panel-filters">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="filter-select"
              >
                <option value="all">Todas</option>
                <option value="execution">Execução</option>
                <option value="system">Sistema</option>
                <option value="queue">Fila</option>
                <option value="user">Usuário</option>
                <option value="performance">Performance</option>
              </select>

              <label className="unread-filter">
                <input
                  type="checkbox"
                  checked={showUnreadOnly}
                  onChange={(e) => setShowUnreadOnly(e.target.checked)}
                />
                Apenas não lidas
              </label>
            </div>

            <div className="notification-panel-list">
              {filteredNotifications.length === 0 ? (
                <div className="notification-empty">
                  <AlertCircle size={48} />
                  <p>Nenhuma notificação</p>
                </div>
              ) : (
                filteredNotifications.map(renderNotificationItem)
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationCenter;

// =============================================
// HOOK PARA USO EXTERNO
// =============================================

export const useNotifications = () => {
  const [notificationCenter, setNotificationCenter] = useState<any>(null);

  const notify = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    if (notificationCenter) {
      notificationCenter.addNotification(notification);
    }
  }, [notificationCenter]);

  return { notify, setNotificationCenter };
};
